import { counterpartyTabs } from './CounterpartyTabs'
import { CounterpartyRecordView } from './CounterpartyRecordView'
import { DealRecordView } from './DealRecordView'
import { InvoiceRecordView } from './InvoiceRecordView'
import { invoiceTabs } from './InvoiceTabs'
import { receivablesTab } from './ReceivablesPanel'
import { contractsTab } from './ContractsPanel'
import { documentTabs } from './DocumentTabs'
import { stockTabs } from './WarehouseTabs'
import { PickRecordView } from './PickRecordView'
import { ReceiptRecordView } from './ReceiptRecordView'
import { ShipmentRecordView } from './ShipmentRecordView'
import { StocktakeRecordView } from './StocktakeRecordView'
import { TransferRecordView } from './TransferRecordView'
// HR cards on the design system (Employee, Department, Vacancy, LeaveRequest, EmployeeKpi).
import { DepartmentRecordView } from './DepartmentRecordView'
import { EmployeeRecordView } from './EmployeeRecordView'
import { KpiRecordView } from './KpiRecordView'
import { LeaveRecordView } from './LeaveRecordView'
import { VacancyRecordView } from './VacancyRecordView'
// #79: лента взаимодействий и след согласований. Добавляются как ещё одна вкладка к
// уже существующим, а не вместо них.
import { approvalsTab, timelineTab } from './TimelineTab'
import type { CardDescriptor } from './types'
import { contactDealsTab } from './ContactTabs'
import { saveContact } from './contactApi'
import { saveCounterparty } from './counterpartyApi'
import { saveDeal } from './dealApi'
import { duplicatesTab } from './CounterpartyMergeTab'
// Карточки продаж и финансов на дизайн-системе (Lead, Contact, Quote, Product, PrimaryDoc,
// Contract, Payment .dc.html).
import { LeadRecordView } from './LeadRecordView'
import { ContactRecordView } from './ContactRecordView'
import { QuoteRecordView } from './QuoteRecordView'
import { ProductRecordView } from './ProductRecordView'
import { DocumentRecordView } from './DocumentRecordView'
import { ContractRecordView } from './ContractRecordView'
import { SubscriptionRecordView } from './SubscriptionRecordView'
import { PaymentRecordView } from './PaymentRecordView'
import { SupportTicketRecordView } from './SupportTicketRecordView'

/**
 * Описания карточек по сущностям (тикет #66, ТЗ §5.1).
 *
 * Данные, а не код: карточка контрагента отличается от карточки сделки составом секций, и
 * ничем больше. Отдельный компонент на сущность означал бы, что оптимистичную блокировку,
 * полевые права и вкладки надо повторить в каждом — и в одном из них однажды забудут.
 *
 * Имена колонок берутся из каталога сервера (`ListCatalog`), а не придумываются:
 * колонки, которой там нет, карточка просто не покажет, и опечатка выглядит как
 * «поле пропало по правам». Пропущенная колонка каталога — это не ошибка: она
 * отфильтруется вместе с теми, на которые нет полевого права.
 */
export const CARD_DESCRIPTORS: Record<string, CardDescriptor> = {
  // Лид (#101). Поля правятся общим сохранением по колонкам: у лида импортируемые колонки
  // объявлены, а доменного PUT нет — статус, ответственный и конверсия меняются действиями.
  'sales.lead': {
    entity: 'sales.lead',
    resource: 'sales.lead',
    titleKey: 'nav.leads',
    titleColumns: ['title'],
    sections: [
      { labelKey: 'card.section.general', columns: ['title', 'source', 'status', 'rejection_reason'] },
      { labelKey: 'card.section.contacts', columns: ['contact_name', 'company_name', 'inn', 'phone', 'email'] },
      { labelKey: 'card.section.leadSla', columns: ['responsible_employee_id', 'first_response_due_at', 'first_response_at'] },
      { labelKey: 'card.section.leadChannel', columns: ['utm_source', 'utm_medium', 'utm_campaign'] },
      { labelKey: 'card.section.service', columns: ['converted_at', 'converted_deal_id', 'created_at'] },
    ],
    // Статус, ответственный, сроки SLA и конверсия — следствия действий, а не поля формы.
    readOnlyColumns: [
      'source', 'status', 'rejection_reason', 'responsible_employee_id', 'first_response_due_at',
      'first_response_at', 'converted_at', 'converted_deal_id', 'created_at',
    ],
    // Сконвертированный лид — история: его данные ушли в контрагента и контакт.
    editable: (values) => values.status !== 'converted' && values.status !== 'rejected',
    // Действия над лидом (квалификация, конверсия, отказ, слияние) — в шапке раскладки
    // (Lead.dc.html), а не отдельной вкладкой «Действия».
    extraTabs: [timelineTab('lead')],
    view: LeadRecordView,
  },

  'sales.counterparty': {
    entity: 'sales.counterparty',
    resource: 'sales.counterparty',
    titleKey: 'nav.counterparties',
    titleColumns: ['name'],
    sections: [
      { labelKey: 'card.section.general', columns: ['name', 'kind', 'relation', 'industry'] },
      // ИНН и КПП вместе намеренно: уникальность контрагента — по паре, а не по ИНН
      // (филиалы одного юрлица делят ИНН и различаются КПП), и разнесённые по разным
      // секциям они читаются как независимые реквизиты.
      { labelKey: 'card.section.requisites', columns: ['inn', 'kpp', 'legal_name', 'ogrn', 'okpo'] },
      // Адреса (#99): счёт без юридического адреса не примет бухгалтерия контрагента.
      { labelKey: 'card.section.addresses', columns: ['legal_address', 'actual_address'] },
      // Поля физлица (#99): у юрлица пусты, у физлица — ФИО раздельно и дата рождения.
      { labelKey: 'card.section.person', columns: ['last_name', 'first_name', 'middle_name', 'birth_date'] },
      { labelKey: 'card.section.contacts', columns: ['email', 'phone', 'website'] },
      // credit_limit живёт за собственным полевым грантом: сервер не опишет эту колонку
      // тому, у кого гранта нет, и секция исчезнет целиком.
      // is_blocked здесь нет (#99): блокировка — доменное действие с причиной, доменный PUT
      // её не принимает, и флажок в форме вёл бы к гарантированному отказу. Состояние
      // блокировки с причиной показывает вкладка «Финансы».
      { labelKey: 'card.section.finance', columns: ['credit_limit', 'payment_terms_days'] },
      { labelKey: 'card.section.service', columns: ['responsible_employee_id', 'created_at'] },
    ],
    // Доменный PUT с версией (#99): любое поле карточки правится одной транзакцией, а
    // не записью по колонке, которая шла мимо проверок контрагента.
    save: saveCounterparty,
    // Контакты, взаимодействия и финансы — тикет #70. Это содержимое ровно этой сущности,
    // и объявлять его в каркасе значило бы, что карточка сделки узнаёт про банковские
    // счета. Права на финансы читает сама вкладка: `extraTabs` собирается на импорте
    // модуля, и переданные сюда права застыли бы на первом вошедшем пользователе.
    // Дебиторка (#72) — отдельной вкладкой от «Финансов» (#70): та отвечает на «можно
    // ли с ним работать», эта — на «сколько должен и что мы с этим делаем». Ageing в
    // обеих читается из одного эндпоинта, поэтому цифра одна.
    // Договоры (#73) — своей вкладкой от «Финансов» (#70): та отвечает на «можно ли с
    // ним работать» (лимит, долг, блокировка), эта — «на каком основании и от чьего
    // лица». Их читают в разные моменты и разные люди.
    extraTabs: [...counterpartyTabs(), receivablesTab(), contractsTab(), timelineTab('counterparty'), duplicatesTab()],
    // Раскладка по дизайн-системе (Company.dc.html): шапка, строка просрочки, факты и пять
    // вкладок. Вкладки выше — те же разделы, разложенные по ним (см. CounterpartyRecordView);
    // остаются объявленными для прежней раскладки и её тестов.
    view: CounterpartyRecordView,
  },

  // Контактное лицо (#98). Сохраняется доменным PUT, а не записью по колонке: одна
  // транзакция с версией, и правило «контрагент контакта не меняется» работает, а не
  // обходится массовым обновлением.
  'sales.contact': {
    entity: 'sales.contact',
    resource: 'sales.contact',
    titleKey: 'nav.contacts',
    titleColumns: ['last_name'],
    sections: [
      { labelKey: 'card.section.general', columns: ['last_name', 'first_name', 'middle_name', 'job_title'] },
      { labelKey: 'card.section.contacts', columns: ['phone', 'mobile_phone', 'email', 'telegram'] },
      { labelKey: 'card.section.service', columns: ['counterparty_id', 'is_primary', 'created_at'] },
    ],
    save: saveContact,
    extraTabs: [contactDealsTab(), timelineTab('contact')],
    view: ContactRecordView,
  },

  // Коммерческое предложение (Quote.dc.html). Поля КП — следствия: итоги считают строки,
  // статус меняют отправка и ответ клиента, номер выдаёт нумератор; доменного PUT нет, и
  // `importable` пуст — поле ввода вело бы к гарантированному отказу.
  'sales.quote': {
    entity: 'sales.quote',
    resource: 'sales.quote',
    titleKey: 'nav.quotes',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'status', 'counterparty_id', 'deal_id'] },
      { labelKey: 'card.section.dates', columns: ['issued_on', 'valid_until', 'sent_at'] },
      { labelKey: 'card.section.money', columns: ['currency', 'subtotal', 'discount_total', 'vat_total', 'total'] },
      { labelKey: 'card.section.service', columns: ['organization_id', 'created_at'] },
    ],
    readOnlyFields: true,
    extraTabs: [approvalsTab('quote')],
    view: QuoteRecordView,
  },

  'sales.deal': {
    entity: 'sales.deal',
    resource: 'sales.deal',
    titleKey: 'nav.deals',
    titleColumns: ['title', 'number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'title', 'status'] },
      { labelKey: 'card.section.money', columns: ['amount', 'currency', 'probability'] },
      { labelKey: 'card.section.pipeline', columns: ['funnel_id', 'stage_id', 'expected_close_on', 'closed_at'] },
      {
        labelKey: 'card.section.service',
        columns: ['counterparty_id', 'responsible_employee_id', 'department_id', 'created_at'],
      },
    ],
    // Раскладка по дизайн-системе (Deal.dc.html): шапка, полоса стадий, факты, лента и
    // четыре вкладки. Прежние вкладки (строки, участники, действия, история стадий, лента,
    // связанные, доп. поля, журнал, вложения) распределены по ним — см. DealRecordView.
    // История стадий по-прежнему отдельна от журнала изменений: та — «как сделка шла по
    // воронке», этот — «кто правил поле».
    view: DealRecordView,
    // Доменный PUT с версией (#100). До него у сделки не было правимых колонок вовсе
    // (`importable` пуст), и кнопка правки вела к гарантированному отказу.
    save: saveDeal,
    // Номер выдаёт нумератор, сумму считают позиции, стадию, вероятность и статус меняет
    // перенос по воронке (история, события, автоматизация). Поле ввода на их месте
    // предлагало бы правку, которую доменный PUT обязан отвергнуть.
    readOnlyColumns: [
      'number', 'status', 'amount', 'probability', 'funnel_id', 'stage_id', 'closed_at',
      'department_id', 'created_at',
    ],
    // Закрытую сделку не правят: переоткрывают переносом на открытую стадию (полоса стадий).
    editable: (values) => values.status === 'open',
  },

  'billing.invoice': {
    entity: 'billing.invoice',
    resource: 'billing.invoice',
    titleKey: 'nav.invoices',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'status', 'counterparty_id'] },
      // Даты вместе: срок оплаты имеет смысл только рядом с датой выставления — «через
      // месяц» и «месяц назад» это одна и та же колонка `due_on` в разных ситуациях.
      { labelKey: 'card.section.dates', columns: ['issued_on', 'due_on', 'sent_at', 'paid_at'] },
      // Итоги ПОКАЗЫВАЮТСЯ здесь, но правятся только через позиции: `subtotal`,
      // `vat_total` и `total` пересчитывает домен при каждой правке строки, и запись в
      // них напрямую дала бы счёт, чей итог не сходится со своими строками. Разбор — на
      // вкладке «Позиции».
      {
        labelKey: 'card.section.money',
        columns: ['currency', 'subtotal', 'discount_total', 'vat_total', 'total', 'paid_total'],
      },
      {
        labelKey: 'card.section.service',
        columns: ['organization_id', 'deal_id', 'contract_id', 'responsible_employee_id',
          'cancelled_at', 'cancel_reason', 'created_at'],
      },
    ],
    // Поля счёта только читаются: итоги пересчитывает домен при каждой правке позиций, а
    // сервер не принимает запись ни в одну колонку счёта (`importable` пуст). Правки
    // делаются операциями — добавлением строки, отправкой, отменой.
    readOnlyFields: true,
    // Позиции, оплаты, напоминания и действия — тикет #72. Плоской строкой списка счёт не
    // описывается: у него построчный НДС и разнесённые платежи, и показать `total` без
    // разбора значит показать бухгалтеру цифру, которую он не может проверить.
    extraTabs: [...invoiceTabs(), approvalsTab('invoice')],
    // Раскладка по дизайн-системе (Invoice.dc.html): три суммы со шкалой, позиции с НДС
    // построчно, оплаты с разнесением, отправки и напоминания — см. InvoiceRecordView.
    view: InvoiceRecordView,
  },

  'billing.document': {
    entity: 'billing.document',
    resource: 'billing.document',
    titleKey: 'nav.documents',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['document_kind', 'number', 'document_date', 'status'] },
      // Итоги ПОКАЗЫВАЮТСЯ здесь, но не правятся: документ заморожен с момента выпуска —
      // копия у контрагента обязана быть той же, что у нас. Разбор — на вкладке «Строки».
      { labelKey: 'card.section.money', columns: ['currency', 'subtotal', 'vat_total', 'total'] },
      // Подпись и ЭДО рядом, но это РАЗНЫЕ факты: «подписан у нас» и «доставлен оператору»
      // разделяет сетевой вызов, который мог не дойти. Свести их в одну строку значило бы
      // считать неотправленный УПД отправленным.
      { labelKey: 'card.section.signature', columns: ['signed_at', 'edi_status', 'edi_sent_at'] },
      {
        labelKey: 'card.section.service',
        columns: ['counterparty_id', 'organization_id', 'invoice_id', 'contract_id',
          'shipment_id', 'created_at'],
      },
    ],
    // Поля документа не правятся вовсе: выпущенный документ заморожен, а исправление
    // делается перевыпуском — процедура, которую ожидает закон. Кнопка сохранения вела бы
    // к гарантированному отказу сервера (`importable` у документа пуст).
    readOnlyFields: true,
    extraTabs: documentTabs(),
    // Раскладка по дизайн-системе (PrimaryDoc.dc.html): подпись плашкой `.sig` с тремя
    // состояниями, действия по статусу в шапке, перевыпуск с причиной.
    view: DocumentRecordView,
  },

  // Договор (Contract.dc.html, #73). Условия меняются операциями домена — ввод в действие,
  // продление только вперёд, расторжение с причиной; организация и валюта не меняются вовсе.
  'billing.contract': {
    entity: 'billing.contract',
    resource: 'billing.contract',
    titleKey: 'nav.contracts',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'contract_type', 'status', 'counterparty_id'] },
      { labelKey: 'card.section.dates', columns: ['signed_on', 'valid_from', 'valid_until'] },
      { labelKey: 'card.section.money', columns: ['currency', 'amount', 'payment_terms_days'] },
      { labelKey: 'card.section.service', columns: ['organization_id', 'created_at'] },
    ],
    readOnlyFields: true,
    view: ContractRecordView,
  },

  // Подписка (#94): ключ каталога `billing.recurring`, право — `billing.invoice`. Жизнь
  // подписки меняется операциями домена (остановить, возобновить), а не формой: поля
  // только для чтения, как у договора.
  'billing.recurring': {
    entity: 'billing.recurring',
    resource: 'billing.invoice',
    titleKey: 'nav.subscriptions',
    titleColumns: ['name'],
    sections: [
      { labelKey: 'card.section.general', columns: ['name', 'counterparty_id', 'contract_id', 'is_active'] },
      { labelKey: 'card.section.dates', columns: ['frequency', 'day_of_month', 'start_date', 'end_date', 'next_run_on'] },
      { labelKey: 'card.section.money', columns: ['currency', 'amount'] },
      { labelKey: 'card.section.service', columns: ['organization_id', 'created_at'] },
    ],
    readOnlyFields: true,
    view: SubscriptionRecordView,
  },

  'warehouse.receipt': {
    entity: 'warehouse.receipt',
    resource: 'warehouse.receipt',
    titleKey: 'nav.receipts',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'status', 'warehouse_id'] },
      // Поставщик и его накладная рядом: расхождение предъявляют по номеру документа
      // поставщика, а не по нашему, и разнесённые по секциям они читаются как
      // независимые реквизиты.
      {
        labelKey: 'card.section.requisites',
        columns: ['supplier_id', 'purchase_order_id', 'supplier_document_number'],
      },
      { labelKey: 'card.section.dates', columns: ['received_at', 'received_by'] },
      { labelKey: 'card.section.service', columns: ['is_cross_dock', 'comment', 'created_at'] },
    ],
    // Приёмка правится только операциями: её строки пересчитывает кладовщик на ТСД, и
    // форма, ставящая принятое количество, дала бы приход без пересчёта, который его
    // объясняет. Сервер это и обеспечивает - `importable` у сущности пуст.
    readOnlyFields: true,
    // Раскладка по дизайн-системе (Receiving.dc.html): строки, расхождения тремя видами,
    // проведение в шапке — см. ReceiptRecordView. Прежние вкладки (#74) перенесены туда.
    view: ReceiptRecordView,
  },

  'warehouse.shipment': {
    entity: 'warehouse.shipment',
    resource: 'warehouse.shipment',
    titleKey: 'nav.shipments',
    titleColumns: ['number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'status', 'counterparty_id'] },
      // Четыре момента вместе: они образуют историю одной отгрузки, и разрыв между
      // проведением и передачей - это и есть сутки на доке, о которых спрашивает клиент.
      {
        labelKey: 'card.section.dates',
        columns: ['posted_at', 'handed_over_at', 'shipped_at', 'delivered_at'],
      },
      {
        labelKey: 'card.section.logistics',
        columns: ['carrier', 'tracking_number', 'packages_count', 'gross_weight_kg',
          'net_weight_kg', 'volume_m3', 'delivery_address'],
      },
      {
        labelKey: 'card.section.service',
        columns: ['warehouse_id', 'order_id', 'primary_document_id', 'created_at'],
      },
    ],
    readOnlyFields: true,
    // Shipment.dc.html: проведение и передача перевозчику — разные действия шапки.
    view: ShipmentRecordView,
  },

  'warehouse.pick': {
    entity: 'warehouse.pick',
    resource: 'warehouse.pick',
    titleKey: 'nav.picking',
    titleColumns: ['number'],
    sections: [
      {
        labelKey: 'card.section.general',
        columns: ['number', 'status', 'pick_mode', 'assignee_employee_id'],
      },
      { labelKey: 'card.section.dates', columns: ['started_at', 'completed_at', 'created_at'] },
      { labelKey: 'card.section.service', columns: ['warehouse_id'] },
    ],
    readOnlyFields: true,
    // Picking.dc.html: маршрут обхода в порядке сервера, назначение и завершение в шапке.
    view: PickRecordView,
  },

  'warehouse.stocktake': {
    entity: 'warehouse.stocktake',
    resource: 'warehouse.stocktake',
    titleKey: 'nav.stocktakes',
    titleColumns: ['number'],
    sections: [
      // Охват и слепота стоят в общей секции, а не в служебной: обе определяют, что
      // именно доказывает этот документ, и прятать их вниз значит показать результат
      // пересчёта, не сказав, чего он стоит.
      {
        labelKey: 'card.section.general',
        columns: ['number', 'status', 'scope', 'zone_id', 'is_blind'],
      },
      // Утверждение отдельно от завершения: первое меняет остаток, второе фиксирует, что
      // посчитали, и одна дата на оба скрыла бы, кто именно принял решение о деньгах.
      {
        labelKey: 'card.section.dates',
        columns: ['started_at', 'completed_at', 'approved_at', 'approved_by'],
      },
      { labelKey: 'card.section.service', columns: ['warehouse_id', 'created_at'] },
    ],
    readOnlyFields: true,
    // Inventory.dc.html: слепой лист без ожидаемого, утверждение — отдельным действием.
    view: StocktakeRecordView,
  },

  'warehouse.transfer': {
    entity: 'warehouse.transfer',
    resource: 'warehouse.transfer',
    titleKey: 'nav.transfers',
    titleColumns: ['number'],
    sections: [
      {
        labelKey: 'card.section.general',
        columns: ['number', 'status', 'from_warehouse_id', 'to_warehouse_id'],
      },
      // Отправлено и принято - два момента, между которыми товар ничей: он ушёл с остатка
      // отправителя и ещё не встал на остаток получателя. Ровно в этом промежутке живёт
      // потеря в пути.
      { labelKey: 'card.section.dates', columns: ['dispatched_at', 'received_at', 'created_at'] },
    ],
    readOnlyFields: true,
    // Transfer.dc.html: отправлено, получено, потери в пути; приёмка — в шапке.
    view: TransferRecordView,
  },

  'warehouse.stock': {
    entity: 'warehouse.stock',
    resource: 'warehouse.stock',
    titleKey: 'nav.stock',
    // Своего имени у остатка нет: он координата «что и где». Заголовок — названия товара и
    // ячейки (подписи ссылок механизма списков); идентификатор в заголовке ничего не
    // говорит человеку, а «товар · ячейка» отличает остаток от соседнего того же товара.
    titleColumns: ['product_id__label', 'cell_id__label', 'warehouse_id__label'],
    titleJoined: true,
    sections: [
      {
        labelKey: 'card.section.general',
        columns: ['warehouse_id', 'zone_id', 'cell_id', 'product_id', 'batch_id'],
      },
      // Количество и зарезервированное рядом: "лежит на полке" и "можно продать" - разные
      // цифры, и одна из них обязательно окажется не той, которую спрашивали.
      { labelKey: 'card.section.quantity', columns: ['quantity', 'reserved_quantity'] },
      // unit_cost живёт за собственным полевым грантом: сервер не опишет эту колонку тому,
      // у кого гранта нет, и секция исчезнет целиком. Гранта сейчас нет ни у кого - см.
      // ListCatalog, это осознанный выбор направления.
      { labelKey: 'card.section.finance', columns: ['unit_cost'] },
      {
        labelKey: 'card.section.service',
        columns: ['handling_unit_id', 'owner_counterparty_id', 'updated_at'],
      },
    ],
    // Остаток меняется только движением (приход, отбор, корректировка по инвентаризации),
    // и каждое пишет строку в append-only stock_movement. Форма, ставящая количество
    // напрямую, дала бы остаток без движения, которое его объясняет.
    readOnlyFields: true,
    extraTabs: stockTabs(),
  },

  // ------------------------------------------------------------------- кадры (#75)

  'hr.employee': {
    entity: 'hr.employee',
    resource: 'hr.employee',
    titleKey: 'nav.employees',
    // Табельный номер запасным заголовком, а не отчеством: у однофамильцев в списке
    // подчинённых различает именно он, и в кадровом документе стоит тоже он.
    titleColumns: ['last_name', 'personnel_number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['personnel_number', 'last_name', 'first_name', 'middle_name', 'status'] },
      { labelKey: 'card.section.contacts', columns: ['work_email', 'work_phone'] },
      // Подразделение, должность и руководитель вместе: это одно назначение, и
      // разнесённые по секциям они читаются как независимые справочные поля, тогда как
      // меняются они одним действием — переводом.
      { labelKey: 'card.section.assignment', columns: ['department_id', 'position_id', 'manager_id'] },
      { labelKey: 'card.section.dates', columns: ['hired_on', 'terminated_on'] },
      // Оклада в секциях НЕТ, и это не пропуск. Колонка `salary` живёт в
      // crm.employee_assignment (истории назначений), а не в crm.employee, поэтому
      // каталог списков её не объявляет вовсе — см. ListCatalog. Оклад показывает
      // вкладка «Назначения», где он и осмыслен: с датой, с которой действует.
      // Дата рождения и пол — служебное: они нужны кадровику при оформлении, а не
      // каждому, кто открыл карточку посмотреть телефон.
      { labelKey: 'card.section.service', columns: ['birth_date', 'gender', 'organization_id', 'created_at'] },
    ],
    // Приём порождает назначение, чек-лист адаптации и учётную запись, и строка,
    // изменённая мимо EmployeeService, даёт сотрудника без всего этого. Сервер это и
    // обеспечивает — `importable` у сущности пуст; флаг убирает кнопку, ведущую к
    // гарантированному отказу.
    readOnlyFields: true,
    view: EmployeeRecordView,
  },

  'hr.department': {
    entity: 'hr.department',
    resource: 'hr.department',
    titleKey: 'nav.departments',
    titleColumns: ['name', 'code'],
    sections: [
      { labelKey: 'card.section.general', columns: ['code', 'name', 'parent_id'] },
      { labelKey: 'card.section.assignment', columns: ['head_employee_id', 'cost_center'] },
      // `path` — служебное: по нему ходит область видимости DEPARTMENT, и правится он
      // только перемещением узла, которое переписывает пути всего поддерева.
      { labelKey: 'card.section.service', columns: ['path', 'organization_id', 'created_at'] },
    ],
    readOnlyFields: true,
    view: DepartmentRecordView,
  },

  'hr.vacancy': {
    entity: 'hr.vacancy',
    resource: 'hr.vacancy',
    titleKey: 'nav.vacancies',
    titleColumns: ['title'],
    sections: [
      { labelKey: 'card.section.general', columns: ['title', 'status', 'department_id', 'position_id'] },
      // Вилка — это предложение кандидату, а не оклад конкретного человека: она
      // печатается в объявлении и полевого права поэтому не требует, в отличие от salary.
      { labelKey: 'card.section.money', columns: ['salary_from', 'salary_to', 'currency'] },
      { labelKey: 'card.section.assignment', columns: ['recruiter_id', 'hiring_manager_id'] },
      { labelKey: 'card.section.dates', columns: ['opened_on', 'target_close_on', 'closed_on'] },
      { labelKey: 'card.section.service', columns: ['external_source', 'external_id', 'created_at'] },
    ],
    readOnlyFields: true,
    view: VacancyRecordView,
  },

  'hr.leave': {
    entity: 'hr.leave',
    resource: 'hr.leave',
    titleKey: 'nav.leaves',
    // Заголовка-имени у заявки нет: она не сущность с названием, а период. Пустой список
    // даёт заголовок из идентификатора, и это честнее выдуманного имени.
    titleColumns: [],
    sections: [
      { labelKey: 'card.section.general', columns: ['employee_id', 'leave_type', 'status'] },
      // Даты и число дней вместе: рабочих дней в периоде меньше календарных, и цифра
      // рядом с датами отвечает на вопрос «сколько спишется», который иначе считают в уме.
      { labelKey: 'card.section.dates', columns: ['start_date', 'end_date', 'days_count'] },
      { labelKey: 'card.section.service', columns: ['comment', 'rejection_reason', 'created_at'] },
    ],
    // Маршрут согласования строится из оргструктуры в момент подачи, и заявка,
    // изменённая мимо LeaveService, — это отпуск, который никто не согласовывал.
    readOnlyFields: true,
    view: LeaveRecordView,
  },

  'hr.kpi': {
    entity: 'hr.kpi',
    resource: 'hr.kpi',
    titleKey: 'nav.kpi',
    titleColumns: [],
    sections: [
      { labelKey: 'card.section.general', columns: ['kpi_id', 'employee_id', 'department_id'] },
      { labelKey: 'card.section.dates', columns: ['period_start', 'period_end'] },
      // План и факт двумя колонками, а не процентом: процент теряет масштаб, а он и есть
      // предмет разговора о премии. `bonus_amount` живёт за полевым грантом `salary` —
      // сервер не опишет эту колонку тому, у кого гранта нет.
      { labelKey: 'card.section.money', columns: ['target_value', 'actual_value', 'bonus_amount', 'currency'] },
      { labelKey: 'card.section.service', columns: ['created_at'] },
    ],
    // Факт пересчитывается из источника и вручную не задаётся (правка дожила бы до
    // первого пересчёта), план заводится сервисом, который сверяет сумму весов.
    readOnlyFields: true,
    view: KpiRecordView,
  },

  'sales.product': {
    entity: 'sales.product',
    resource: 'sales.product',
    titleKey: 'nav.products',
    titleColumns: ['name', 'sku'],
    sections: [
      { labelKey: 'card.section.general', columns: ['sku', 'name', 'product_type', 'category_id'] },
      { labelKey: 'card.section.money', columns: ['tax_rate_code'] },
      { labelKey: 'card.section.logistics', columns: ['barcode', 'weight_kg', 'min_stock'] },
      { labelKey: 'card.section.service', columns: ['is_active', 'created_at'] },
    ],
    // Правка — доменной формой товара (ProductEditSheet): запись по колонке через
    // bulk/update прошла бы мимо правила «признаки учёта после движения не меняются».
    readOnlyFields: true,
    view: ProductRecordView,
  },

  // Оплата (#78). Карточка платежа read-only по существу: сумма, дата и плательщик
  // приходят из банковской выписки, и правка их на экране означала бы, что база
  // расходится с тем, что сказал банк, — а нетронутая строка выписки это единственное,
  // что скажет, чья сторона сдвинулась. Разнесение и сторнирование делаются действиями
  // (`/billing/payments/...`), а не редактированием полей.
  'payments.payment': {
    entity: 'payments.payment',
    resource: 'payments.payment',
    titleKey: 'nav.paymentRegistry',
    titleColumns: ['number', 'purpose'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'direction', 'method', 'status'] },
      // Не разнесённый остаток стоит рядом с суммой намеренно: разница между ними и есть
      // вопрос, ради которого карточку открывают.
      { labelKey: 'card.section.money', columns: ['amount', 'currency', 'unallocated_amount'] },
      // Плательщик из платёжки, а не контрагент: у нераспознанного платежа контрагента
      // нет, и секция из одного пустого поля выглядела бы как поломка. Имя и ИНН из
      // банка есть всегда — с них и начинают разбор.
      { labelKey: 'card.section.requisites', columns: ['payer_name', 'payer_inn', 'counterparty_id'] },
      { labelKey: 'card.section.service', columns: ['payment_date', 'value_date', 'bank_account_id', 'organization_id', 'external_id', 'created_at'] },
    ],
    // Разнесение по счетам, подтверждение и сторно — в раскладке (Payment.dc.html).
    view: PaymentRecordView,
  },

  // Обращения клиентов (#123). Поля не правятся формой: статус двигают ответ и переходы,
  // исполнителя — назначение с проверкой области на сервере. Всё это — в раскладке.
  'support.ticket': {
    entity: 'support.ticket',
    resource: 'support.ticket',
    titleKey: 'nav.support',
    titleColumns: ['subject', 'number'],
    sections: [
      { labelKey: 'card.section.general', columns: ['number', 'subject', 'kind', 'status', 'source'] },
      { labelKey: 'card.section.service', columns: ['counterparty_id', 'assigned_user_id', 'first_response_due_at', 'first_answer_at', 'created_at'] },
    ],
    readOnlyFields: true,
    view: SupportTicketRecordView,
  },
}

/**
 * Описание карточки для сущности, если оно есть.
 *
 * Отсутствие — обычный случай: карточка объявлена не для каждой сущности каталога, и
 * выдумывать её из колонок значило бы показать пользователю экран из `stage_id` и
 * `department_id` без единой группировки, выдав его за спроектированный.
 */
export function cardDescriptor(entity: string): CardDescriptor | null {
  return CARD_DESCRIPTORS[entity] ?? null
}
